<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import LeadsAPI from 'dashboard/api/leads';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import CreateLeadDialog from 'dashboard/components-next/Crm/Conversation/CreateLeadDialog.vue';

const createDialogRef = ref(null);
function openCreate() { createDialogRef.value?.open(); }
function onCreated(lead) {
  // Recarrega lista
  fetchLeads();
}

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const leads = ref([]);
const isLoading = ref(false);

const contactId = computed(() => route.params.contactId);
const accountId = computed(() => route.params.accountId);

const statusMeta = {
  in_pipeline: { color: 'bg-n-brand/10 text-n-brand', label: 'Em andamento' },
  qualified:   { color: 'bg-n-amber-3 text-n-amber-12', label: 'Qualificado' },
  won:         { color: 'bg-n-grass-3 text-n-grass-12', label: 'Ganho' },
  lost:        { color: 'bg-n-ruby-3 text-n-ruby-12', label: 'Perdido' },
  discarded:   { color: 'bg-n-alpha-2 text-n-slate-11', label: 'Descartado' },
};

function statusBadge(status) {
  return statusMeta[status] || { color: 'bg-n-alpha-2 text-n-slate-11', label: status };
}

function classificationLabel(c) {
  return { hot: 'Quente', warm: 'Morno', cold: 'Frio' }[c] || null;
}

function formatMoney(cents) {
  if (cents == null) return null;
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cents / 100);
}

function formatDate(iso) {
  if (!iso) return '';
  return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(iso));
}

async function fetchLeads() {
  if (!contactId.value) return;
  isLoading.value = true;
  try {
    const { data } = await LeadsAPI.index({ contact_id: contactId.value, per_page: 50 });
    leads.value = data?.leads || [];
  } catch {
    leads.value = [];
  } finally {
    isLoading.value = false;
  }
}

function openLead(lead) {
  if (lead.pipeline?.id) {
    router.push(`/app/accounts/${accountId.value}/pipelines/${lead.pipeline.id}?lead=${lead.id}`);
  } else {
    router.push(`/app/accounts/${accountId.value}/pipelines`);
  }
}

function goToPipelines() {
  router.push(`/app/accounts/${accountId.value}/pipelines`);
}

onMounted(fetchLeads);
watch(contactId, fetchLeads);
</script>

<template>
  <div v-if="isLoading" class="flex items-center justify-center py-10 text-n-slate-11">
    <Spinner />
  </div>

  <div v-else-if="leads.length" class="px-6 py-4 flex flex-col gap-3">
    <div class="flex justify-end">
      <NextButton size="sm" icon="i-lucide-plus" label="Novo lead" @click="openCreate" />
    </div>
    <ol class="flex flex-col gap-2">
      <li
        v-for="lead in leads"
        :key="lead.id"
        class="rounded-xl border border-n-weak hover:border-n-slate-7 transition-colors p-3 cursor-pointer"
        @click="openLead(lead)"
      >
        <div class="flex items-center justify-between gap-2 min-w-0">
          <div class="flex items-center gap-2 min-w-0">
            <span class="size-2 rounded-full" :style="{ background: lead.pipeline?.name ? '#6366f1' : '#94a3b8' }" />
            <p class="text-heading-3 text-n-slate-12 truncate mb-0">
              {{ lead.pipeline?.name || 'Pipeline removido' }}
            </p>
          </div>
          <span
            class="text-label-small px-1.5 py-0.5 rounded font-medium uppercase tracking-wide shrink-0"
            :class="statusBadge(lead.status).color"
          >{{ statusBadge(lead.status).label }}</span>
        </div>

        <div class="flex items-center gap-3 mt-1.5 text-body-small text-n-slate-11 flex-wrap">
          <span v-if="lead.stage?.name" class="inline-flex items-center gap-1">
            <i class="i-lucide-layers size-3" />
            {{ lead.stage.name }}
          </span>
          <span v-if="classificationLabel(lead.classification)" class="inline-flex items-center gap-1">
            <i class="i-lucide-flame size-3" />
            {{ classificationLabel(lead.classification) }}
          </span>
          <span v-if="lead.value_cents" class="inline-flex items-center gap-1 text-n-grass-11">
            <i class="i-lucide-coins size-3" />
            {{ formatMoney(lead.value_cents) }}
          </span>
          <span class="inline-flex items-center gap-1 ml-auto">
            <i class="i-lucide-clock size-3" />
            {{ formatDate(lead.last_activity_at || lead.created_at) }}
          </span>
        </div>

        <p v-if="lead.summary" class="text-body-small text-n-slate-11 mt-1.5 mb-0 line-clamp-2">
          {{ lead.summary }}
        </p>
      </li>
    </ol>
  </div>

  <div v-else class="px-6 py-8 flex flex-col items-center gap-3 text-center">
    <p class="text-body-small text-n-slate-11 max-w-xs mb-0">
      Nenhum lead para este contato ainda. Leads aparecem aqui quando uma
      conversa é classificada com intenção de compra pela IA, ou criados
      manualmente.
    </p>
    <div class="flex items-center gap-2">
      <NextButton size="sm" icon="i-lucide-plus" label="Criar lead" @click="openCreate" />
      <NextButton size="sm" faded slate label="Ir para Pipelines" @click="goToPipelines" />
    </div>
  </div>

  <!-- Dialog inline (sem conversation_id já que estamos no contato) -->
  <CreateLeadDialog
    ref="createDialogRef"
    :contact-id="contactId"
    @created="onCreated"
  />
</template>
